<template>
    <div>
        <JhmaHeader/>
        <article class="start-page">
            <transition :name="confirm ? 'right' : 'left'" mode="out-in">
                <StartConfirm v-if="confirm"
                              :request="request"
                              :payment="payment"
                              :is-processing="isProcessing"
                              :price="price"
                              @cancel="confirm = false"
                              @submit="onSubmit"/>
                <div v-if="!confirm">
                    <FormHeader/>
                    <ShowStep :current="1"/>
                    <form @submit.prevent="onConfirm" class="pt-4 sm:pt-14">
                        <SectionLabel>申込者情報</SectionLabel>
                        <section class="content">
                            <div class="form-group">
                                <FormLabel required>名前</FormLabel>
                                <div class="col-span-8 flex flex-row w-form-row max-w-full">
                                    <div class="flex-1 pr-2">
                                        <FormInput type="text" class="w-full" autocomplete="family-name"
                                                   placeholder="山田" v-model="request.name1"/>
                                        <ErrorText name="name1" :errors="errors"/>
                                    </div>
                                    <div class="flex-1 pl-2">
                                        <FormInput type="text" class="w-full" autocomplete="given-name"
                                                   placeholder="太郎" v-model="request.name2"/>
                                        <ErrorText name="name2" :errors="errors"/>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <FormLabel required>フリガナ</FormLabel>
                                <div class="col-span-8 flex flex-row w-form-row max-w-full">
                                    <div class="flex-1 pr-2">
                                        <FormInput type="text" class="w-full" placeholder="ヤマダ" v-model="request.kana1"/>
                                        <ErrorText name="kana1" :errors="errors"/>
                                    </div>
                                    <div class="flex-1 pl-2">
                                        <FormInput type="text" class="w-full" placeholder="タロウ" v-model="request.kana2"/>
                                        <ErrorText name="kana2" :errors="errors"/>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <FormLabel required>
                                    <div class="flex flex-row items-center">
                                        <span>郵便番号</span>
                                        <div class="ml-4 block lg:hidden">
                                            <button type="button"
                                                    class="rounded-full text-base transition bg-main-color text-white text-sm py-1 px-4"
                                                    @click="onAddressSuggest">
                                                郵便番号検索 >
                                            </button>
                                        </div>
                                    </div>
                                </FormLabel>
                                <div class="col-span-8 flex flex-row flex-wrap w-form-row max-w-full">
                                    <div class="w-full lg:w-auto lg:flex-1 lg:pr-2">
                                        <FormInput type="text" v-model="request.postcode" class="w-full"
                                                   placeholder="000-0000"
                                                   maxlength="8"
                                                   autocomplete="postal-code" :half-width="true"/>
                                        <ErrorText name="postcode" :errors="errors"/>
                                    </div>
                                    <div class="lg:flex-1 lg:pl-2">
                                        <button type="button"
                                                class="h-full w-[167px] h-[44px] rounded-full text-base transition bg-main-color p-1 hidden lg:block"
                                                @click="onAddressSuggest">
                                             <span class="flex items-center justify-center text-white font-medium relative">
                                        郵便番号検索
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                             stroke-width="1.5" stroke="currentColor"
                                             class="w-6 h-6 absolute right-2 top-0 bottom-0 my-auto">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                        </svg>
                                    </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <PrefPicker v-model="request.pref" :errors="errors"/>
                            <FormGroup name="city" label="市区町村" autocomplete="address-level2"
                                       placeholder="中央区"
                                       v-model="request.city" :errors="errors" required/>
                            <FormGroup name="address" label="それ以降の住所" autocomplete="address-line1"
                                       placeholder="銀座七丁目15番8号"
                                       v-model="request.address" :errors="errors" required/>
                            <FormGroup name="bill" label="マンション・建物名" autocomplete="address-line2"
                                       v-model="request.bill" :errors="errors"/>
                            <FormGroup label="電話番号" name="tel" autocomplete="tel" half-width
                                       placeholder="09012345678" required
                                       help-text="※ハイフン（-）無しで入力してください"
                                       v-model="request.tel" :errors="errors"/>
                            <FormGroup label="メールアドレス" name="email" autocomplete="email"
                                       placeholder="info@example.com" required
                                       v-model="request.email" :errors="errors">
                                <template v-slot:helpText>
                                    ※入力ミスや受信拒否設定をされている場合は、当協会からのメールが届きません。<br/>
                                    ※申込後の自動返信メールが届かない場合は協会までお問い合わせください。<br/>
                                    ※携帯メールアドレスをご使用の場合、届かない可能性があります。 <br/>
                                    必ず@regist.jhma.or.jpからのメールが受信できるように設定してください。<br/>
                                </template>
                            </FormGroup>
                            <FormGroup label="メールアドレス(確認用)" name="email_confirmation"
                                       placeholder="info@example.com" required
                                       v-model="request.email_confirmation" :errors="errors"/>
                            <IndustryForm v-model="request.industry" :errors="errors"/>
                            <TriggerForm v-model="request.trigger" :errors="errors"/>
                            <FormGroup v-if="request.trigger === 'その他'" required label="その他のきっかけ" name="other_trigger" placeholder="内容を記入してください" v-model="request.other_trigger" :errors="errors"/>
                            <div class="form-group">
                                <FormLabel required>現在公式テキストをお持ちですか?</FormLabel>
                                <div class="col-span-8">
                                    <div class="flex items-center gap-4">
                                        <label>
                                            <input type="radio" name="has_text" value="1"
                                                   v-model="request.has_text"/>
                                            <span class="ml-2">はい</span>
                                        </label>
                                        <label>
                                            <input type="radio" name="has_text" value="0"
                                                   v-model="request.has_text"/>
                                            <span class="ml-2">いいえ</span>
                                        </label>
                                    </div>
                                    <ErrorText :errors="errors" name="has_text"/>
                                </div>
                            </div>
                            <div class="form-group">
                                <FormLabel required>お支払い方法の選択</FormLabel>
                                <div class="flex-1 col-span-8">
                                    <FormInput type="select" v-model="request.payment_type"
                                               :options="paymentTypes" class="lg:w-200p"/>
                                    <ErrorText :errors="errors" name="payment_type"/>
                                    <CreditForm
                                        v-if="request.payment_type === 'credit'"
                                        @onChangePayTimes="e => request.pay_times = e"
                                        v-model="payment.credit" :errors="errors"/>
                                </div>
                            </div>
                        </section>
                        <section v-if="request.payment_type === 'later'"
                                 class="content" style="margin-top: 16px;display: flex; justify-content: center">
                            <img src="/images/gmo_banner.gif" alt="GMO後払い" style="width: 100%; max-width: 187px"/>
                        </section>
                        <SectionLabel  v-if="request.payment_type === 'later'">コンビニ後払い（GMO後払い）に関する注意事項</SectionLabel>
                        <section v-if="request.payment_type === 'later'"
                                 class="content"
                                 style="gap: 16px;display: flex;flex-direction: column">
                            <p>
                                GMO後払いとはお客様の手元に商品が到着した後に代金をお支払い頂く決済方法です。<br/>
                                請求書は商品と一緒にお届けしますので、全国のコンビニエンスストア、銀行でお支払いください。
                            </p>
                            <p>
                                ■お支払い期限<br/>
                                　請求書発行から14日以内にお支払いください。お支払い期限は請求書にも記載しております。<br/>
                                　お支払い期限を一定期間過ぎてもお支払いの確認がとれない場合、ご請求金額に回収事務手数料297円（税込）が加算されます。（最大3回、合計891円）
                            </p>
                            <p>
                                ■ご注意 後払い手数料：385円（税込）<br/>
                                　GMO後払いは<a href="https://www.gmo-ps.com" target="_blank"
                                               rel="noreferrer" class="link-text">GMOペイメントサービス株式会社</a>が提供する決済サービスです。<br/>
                                　当協会はGMOペイメントサービス株式会社に対しサービスの範囲内で個人情報を提供し、代金債権を譲渡します。<br/>
                                　 GMO後払いサービスの<a href="https://www.gmo-ps.com/doukon-annnai/" target="_blank"
                                                        rel="noreferrer" class="link-text">注意事項</a>および、<a href="https://www.gmo-ps.com/customer/privacy_operation/"
                                                                                                                 target="_blank" rel="noreferrer" class="link-text">プライバシーポリシー</a>に同意のうえ、GMO後払いサービスをご利用ください。<br/>
                                ・ご利用者が未成年の場合、法定代理人の利用同意を得てご利用ください。<br/>
                                ・ご利用にあたり審査がございます。審査結果によっては「GMO後払い」をご利用いただけない場合がございますので、その場合には別のお支払方法へ変更をお願いします。<br/>
                                ・ご利用限度額はGMO後払い累計で、55,000円（税込）です。
                            </p>
                        </section>
                        <SectionLabel>申し込み詳細</SectionLabel>
                        <section class="content">
                            <ApplyDetailTable :price="price" :is-selected="!!request.payment_type"/>
                            <Agreement :errors="errors" v-model="request.agreement"/>
                            <p class="p-2">
                                <ErrorText :errors="errors" name="device_info"/>
                            </p>
                            <div class="form-group text-center">
                                <div class="col-span-12">
                                <button
                                    class="mx-auto w-[246px] h-[55px] text-base transition rounded-full bg-main-color p-1"
                                    :disabled="isProcessing">
                                    <span class="flex items-center justify-center text-white font-medium relative">
                                        申し込み内容の確認
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                             stroke-width="1.5" stroke="currentColor"
                                             class="w-6 h-6 absolute right-2 top-0 bottom-0 my-auto">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                        </svg>
                                    </span>
                                </button>
                                </div>
                            </div>
                        </section>
                    </form>
                </div>
            </transition>
            <input type="hidden" id="fraudbuster" name="fraudbuster" ref="deviceInfo"/>
            <Loading :is-show="isProcessing"/>
        </article>
        <JhmaFooter/>
    </div>
</template>

<script>
import SectionLabel from "../../parts/app/SectionLabel";
import FormGroup from "../../parts/app/FormGroup";
import FormLabel from "../../parts/app/FormLabel";
import FormInput from "../../parts/app/FormInput";
import yubinbango from "../../mixins/yubinbango";
import multiPayment from "../../mixins/multiPayment";
import CardExpireInput from "../../parts/app/CardExpireInput";
import moment from "moment";
import StartConfirm from "../../parts/app/StartConfirm";
import ApplyDetailTable from "../../parts/app/ApplyDetailTable";
import {mapActions, mapState} from "vuex";
import ErrorText from "../../parts/ErrorText";
import SecurityCodeInformation from "../../parts/app/SecurityCodeInformation";
import BrandLogos from "../../parts/app/BrandLogos";
import IndustryForm from "../../parts/app/IndustryForm";
import TriggerForm from "../../parts/app/TriggerForm";
import FormHeader from "../../parts/app/FormHeader";
import Agreement from "../../parts/app/Agreement";
import PrefPicker from "../../parts/app/PrefPicker";
import Loading from "../../parts/app/Loading";
import ShowStep from "../../parts/app/ShowStep";
import CreditForm from "../../parts/app/CreditForm";
import JhmaHeader from "../../parts/app/JhmaHeader.vue";
import JhmaFooter from "../../parts/app/JhmaFooter.vue";

export default {
    name: "Start",
    components: {
        JhmaFooter,
        JhmaHeader,
        CreditForm,
        ShowStep,
        Loading,
        PrefPicker,
        Agreement,
        FormHeader,
        TriggerForm,
        IndustryForm,
        BrandLogos,
        SecurityCodeInformation,
        ErrorText,
        ApplyDetailTable, StartConfirm, CardExpireInput, FormInput, FormLabel, FormGroup, SectionLabel
    },
    mixins: [yubinbango, multiPayment],
    head: {
        title: {
            inner: '在宅看護指導士資格試験申し込み',
            separator: '|',
            complement: '一般社団法人全国在宅医療マネジメント協会'
        },
        script: [
            {
                type: 'text/javascript',
                src: 'https://fraud-buster.appspot.com/js/fraudbuster.js'
            },
        ]
    },

    data() {
        return {
            request: {
                name1: '',
                name2: '',
                kana1: '',
                kana2: '',
                postcode: '',
                pref: null,
                city: '',
                address: '',
                bill: '',
                tel: '',
                email: '',
                email_confirmation: '',
                industry: null,
                trigger: null,
                other_trigger: '',
                has_text: null,
                payment_type: null,
                pay_times: 1,
                agreement: false,
            },
            payment: {
                credit: {
                    cardno: '',
                    expire: moment().format('YYYYMM'),
                    securitycode: '',
                    holdername: ''
                },
                creditResult: {
                    token: null,
                    toBeExpiredAt: null,
                    maskedCardNo: null,
                    isSecurityCodeSet: null,
                }
            },
            confirm: false,
            errors: {}
        }
    },

    computed: {
        paymentTypes() {
            return [
                {
                    value: 'credit',
                    label: 'クレジットカード'
                },
                {
                    value: 'later',
                    label: 'コンビニ後払い'
                }
            ]
        },

        payTimesList() {
            return [
                {
                    value: 1,
                    label: '1回'
                },
                {
                    value: 'bonus',
                    label: 'ボーナス払'
                }
            ]
        },

        isProcessing() {
            return this.$store.state.apply_validate.requestStatus === 'pending' ||
                this.$store.state.apply_exec.requestStatus === 'pending';
        },

        ...mapState('price_get', {
            price: 'data',
        })
    },

    watch: {
        'request.postcode': function (postcode, old) {
            if (!/^\d{0,3}-?\d{0,4}$/.test(postcode)) {
                this.$nextTick(() => {
                    this.request.postcode = old;
                });
            }
        },

        'request.tel': function (tel, old) {
            if (!/^\d*$/.test(tel)) {
                this.$nextTick(() => {
                    this.request.tel = old;
                });
            }
        },

        confirm: function () {
            window.scrollTo({
                top: 0,
            });
        },

        "$store.state.apply_validate.requestStatus": function (status) {
            if (status === 'success') {
                this.resetStatus();
                this.confirm = true;
            } else if (status === 'failed') {
                this.resetStatus();
                this.$toastr.error('入力をご確認ください。');
            }
        },

        "$store.state.apply_exec.requestStatus": function (status) {
            if (status === 'success') {
                const applicationId = this.$store.state.apply_exec.applicationId;
                const base64email = btoa(this.request.email);
                const base64tel = btoa(this.request.tel);
                this.resetExecStatus();
                this.$router.replace({
                    path: '/thank_you',
                    query: {
                        res: applicationId,
                        m: base64email,
                        t: base64tel
                    }
                });
            } else if (status === 'failed') {
                this.resetExecStatus();
                this.confirm = false;
                this.$toastr.error('登録が行えませんでした。');
            }
        },

        "$store.state.apply_validate.errors": function (errors) {
            this.errors = errors;
        },

        "$store.state.apply_exec.errors": function (errors) {
            this.errors = errors;
            if (errors['payment_type']) {
                setTimeout(() => {
                    this.$scrollTo(`#error_payment_type`, {
                        offset: -80
                    });
                }, 500);
            }
        },

        "request.payment_type": async function (payment_type) {
            try {
                if (payment_type) {
                    await this.getPrice(payment_type);
                }
            } catch (e) {
                this.$toastr.error('価格を取得できませんでした。');
            }
        }
    },

    mounted() {
        this.clearErrors();
    },

    methods: {
        ...mapActions('apply_validate', [
            'execValidate',
            'resetStatus',
            'clearErrors',
        ]),
        ...mapActions('apply_exec', [
            'execApply',
            'resetExecStatus',
        ]),
        ...mapActions('price_get', [
            'getPrice',
        ]),

        async onAddressSuggest() {
            const result = await this.resolvePostCode(this.request.postcode);
            this.request = {
                ...this.request,
                pref: result['region'],
                city: result['locality'],
                address: `${result['street']} ${result['extended']}`
            };
        },

        async parseCard() {
            if (this.request.payment_type === 'credit') {
                try {
                    const result = await this.getCardToken(this.payment.credit);
                    this.payment.creditResult = result;
                    return result['token'];
                } catch (e) {
                    this.$toastr.error(e.message);
                    this.confirm = false;
                    return undefined;
                }
            } else {
                return undefined;
            }
        },

        async onConfirm() {
            await this.execValidate({
                ...this.request,
                card_token: await this.parseCard(),
                device_info: this.$refs.deviceInfo.value,
            });
        },

        async onSubmit() {
            await this.execApply({
                ...this.request,
                card_token: await this.parseCard(),
                device_info: this.$refs.deviceInfo.value,
            });
        },
    }
}
</script>

<style scoped>

</style>
